import { defineStore } from "pinia";
import type { ReminderDto } from "~/api";
import { apiClient } from "~/utils/apiClient";

/**
 * Category carried by every support ticket reminder (see SupportReminderCopy on the server).
 * Support reminders are counted separately so they badge the Support Tickets icon rather than
 * the notification bell.
 */
export const SUPPORT_REMINDER_CATEGORY = "SupportTicket";

export const useReminderStore = defineStore("ReminderStore", {
  state: (): {
    reminders: ReminderDto[];
    /**
     * Support ticket reminders, fetched with a category-scoped request. A separate list
     * rather than a filter over `reminders`: that page holds only the 50 most recent across
     * every category, so a busy bell could push every support reminder past the page edge
     * and the Help & Support panel would show empty under a non-zero badge.
     */
    supportReminders: ReminderDto[];
    unreadCount: number;
    /**
     * Server-reported unread count for support reminders only. Held separately from
     * `unreadCount` (which is the total across every category) because both badges must
     * reflect server totals, not the page of reminders currently loaded — `load()` fetches
     * only the first 50, so counting the array would silently under-count past that.
     */
    supportUnread: number;
    totalCount: number;
    loaded: boolean;
  } => ({
    reminders: [],
    supportReminders: [],
    unreadCount: 0,
    supportUnread: 0,
    totalCount: 0,
    loaded: false,
  }),

  getters: {
    /** Unread support ticket reminders — badges the Support Tickets icon. */
    supportUnreadCount: (state) => state.supportUnread,

    /**
     * Unread reminders excluding support ones, so the bell and the Support Tickets badge
     * don't both count the same item. Derived from the two server totals rather than the
     * loaded page; clamped because the two counts are fetched in separate requests and could
     * momentarily disagree if a reminder arrives between them.
     */
    generalUnreadCount: (state) =>
      Math.max(0, state.unreadCount - state.supportUnread),

    /**
     * Reminders for the bell's panel — everything except support tickets, which list under
     * the Help & Support icon instead so one reminder never appears in both panels.
     */
    generalReminders: (state) =>
      state.reminders.filter((r) => r.category !== SUPPORT_REMINDER_CATEGORY),
  },

  actions: {
    async load() {
      try {
        const result = await apiClient.reminder.getApiReminderMy(false, 0, 50);
        this.reminders = result.items ?? [];
        this.unreadCount = result.unreadCount ?? 0;
        this.totalCount = result.totalCount ?? 0;
        this.loaded = true;
      } catch {
        // silent fail on load — bell just shows 0
      }

      // Separate category-scoped request so the Help & Support panel is complete even when
      // the mixed page above is dominated by other categories.
      try {
        const support = await apiClient.reminder.getApiReminderMy(
          false,
          0,
          50,
          SUPPORT_REMINDER_CATEGORY,
        );
        this.supportReminders = support.items ?? [];
      } catch {
        // silent fail — support panel just shows empty
      }

      // Separate request: the support badge counts every unread support reminder, not just
      // the ones in the page above.
      await this.loadSupportUnreadCount();
    },

    async loadUnreadCount() {
      try {
        this.unreadCount = await apiClient.reminder.getApiReminderUnreadCount();
      } catch {
        // silent fail
      }

      await this.loadSupportUnreadCount();
    },

    /** Unread count scoped to support reminders, for the Support Tickets badge. */
    async loadSupportUnreadCount() {
      try {
        this.supportUnread = await apiClient.reminder.getApiReminderUnreadCount(
          SUPPORT_REMINDER_CATEGORY,
        );
      } catch {
        // silent fail
      }
    },

    async markAsRead(guid: string) {
      try {
        await apiClient.reminder.patchApiReminderRead(guid);
        // The same reminder can be present in both the mixed page and the support-scoped
        // list (as distinct objects), so update every copy but decrement only once.
        const copies = [
          this.reminders.find((r) => r.guid === guid),
          this.supportReminders.find((r) => r.guid === guid),
        ].filter((r): r is ReminderDto => r !== undefined);

        if (copies.some((r) => !r.isRead)) {
          this.decrementUnread(copies[0].category);
        }
        copies.forEach((r) => (r.isRead = true));
      } catch {
        // silent fail
      }
    },

    async markAllRead() {
      try {
        await apiClient.reminder.patchApiReminderReadAll();
        this.reminders.forEach((r) => (r.isRead = true));
        this.supportReminders.forEach((r) => (r.isRead = true));
        // Clears everything server-side, including unread reminders beyond the loaded page.
        this.unreadCount = 0;
        this.supportUnread = 0;
      } catch {
        // silent fail
      }
    },

    async deleteReminder(guid: string) {
      try {
        await apiClient.reminder.deleteApiReminder(guid);

        let removed: ReminderDto | undefined;
        for (const list of [this.reminders, this.supportReminders]) {
          const idx = list.findIndex((r) => r.guid === guid);
          if (idx !== -1) removed = list.splice(idx, 1)[0];
        }

        if (removed) {
          if (!removed.isRead) this.decrementUnread(removed.category);
          this.totalCount = Math.max(0, this.totalCount - 1);
        }
      } catch {
        // silent fail
      }
    },

    addFromSignalR(reminder: ReminderDto) {
      if (reminder.guid && this.reminders.some((r) => r.guid === reminder.guid))
        return;
      this.reminders.unshift(reminder);
      this.unreadCount++;
      if (reminder.category === SUPPORT_REMINDER_CATEGORY) {
        this.supportReminders.unshift(reminder);
        this.supportUnread++;
      }
    },

    /**
     * Keeps the total and the support-scoped count in step when one reminder becomes read or
     * is removed. Both are server totals, so they are adjusted rather than recounted from the
     * loaded page.
     */
    decrementUnread(category?: string | null) {
      this.unreadCount = Math.max(0, this.unreadCount - 1);
      if (category === SUPPORT_REMINDER_CATEGORY) {
        this.supportUnread = Math.max(0, this.supportUnread - 1);
      }
    },
  },
});
